import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, useScroll, useSpring } from "framer-motion";
import { ArrowLeft, ArrowRight, Clock, Loader2, Link2, Mail, Facebook, Linkedin, Twitter } from "lucide-react";
import { toast } from "sonner";
import { MaskedLine, FadeUp, Page, Meta } from "../components/Reveal";
import { getArticles } from "../lib/api";
import { usePageMeta } from "../lib/usePageMeta";

function ProgressBar() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 80, damping: 25 });
  return (
    <motion.div
      data-testid="article-progress-bar"
      className="fixed inset-x-0 top-0 z-[60] h-0.5 origin-left bg-clay"
      style={{ scaleX }}
    />
  );
}

function ShareButtons({ title }) {
  const url = window.location.href;
  const networks = [
    { label: "Facebook", Icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, id: "share-facebook" },
    { label: "LinkedIn", Icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, id: "share-linkedin" },
    { label: "X", Icon: Twitter, href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`, id: "share-x" },
    { label: "Courriel", Icon: Mail, href: `mailto:?subject=${encodeURIComponent(`À lire : ${title}`)}&body=${encodeURIComponent(`Une conversation qui pourrait vous intéresser :\n\n${title}\n${url}`)}`, id: "share-email" },
  ];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Lien copié, à partager sans retenue.");
    } catch {
      toast.error("Copie impossible. Sélectionnez l'adresse dans la barre du navigateur.");
    }
  };

  return (
    <div data-testid="article-share" className="mt-16 border-t border-linen pt-10">
      <p className="text-xs font-medium uppercase tracking-[0.25em] text-ash">
        Partager cette conversation
      </p>
      <div className="mt-5 flex flex-wrap items-center gap-3">
        {networks.map(({ label, Icon, href, id }) => (
          <a
            key={id}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Partager sur ${label}`}
            data-testid={id}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-linen text-smoke transition-colors duration-300 hover:border-clay hover:text-clay"
          >
            <Icon className="h-4 w-4" strokeWidth={1.5} />
          </a>
        ))}
        <button
          onClick={copy}
          data-testid="share-copy"
          className="inline-flex h-11 items-center gap-2 rounded-full border border-linen px-5 text-xs font-medium uppercase tracking-[0.15em] text-smoke transition-colors duration-300 hover:border-clay hover:text-clay"
        >
          <Link2 className="h-4 w-4" strokeWidth={1.5} />
          Copier le lien
        </button>
      </div>
    </div>
  );
}

export default function Article() {
  const { slug } = useParams();
  const [articles, setArticles] = useState(null);

  useEffect(() => {
    setArticles(null);
    getArticles()
      .then(setArticles)
      .catch(() => setArticles([]));
  }, [slug]);

  const article = articles ? articles.find((a) => a.slug === slug) : null;
  usePageMeta(article ? { title: article.title, description: article.dek, image: article.img } : null);

  if (articles === null) {
    return (
      <Page testId="article-page">
        <div className="flex min-h-screen items-center justify-center" data-testid="article-loading">
          <Loader2 className="h-6 w-6 animate-spin text-clay" />
        </div>
      </Page>
    );
  }

  if (!article) {
    return (
      <Page testId="article-not-found">
        <div className="mx-auto flex min-h-screen max-w-7xl flex-col items-start justify-center px-6 md:px-12">
          <Meta>Introuvable</Meta>
          <h1 className="mt-6 font-serif text-4xl font-light tracking-tight md:text-6xl">
            Cette conversation n'existe pas <em className="text-clay">(encore).</em>
          </h1>
          <Link to="/conversations" data-testid="article-back-notfound"
            className="group mt-10 inline-flex items-center gap-2 rounded-full bg-clay px-7 py-3.5 text-xs font-medium uppercase tracking-[0.15em] text-cream transition-colors duration-300 hover:bg-charcoal">
            <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
            Toutes les conversations
          </Link>
        </div>
      </Page>
    );
  }

  const idx = articles.findIndex((a) => a.slug === slug);
  const next = articles.length > 1 ? articles[(idx + 1) % articles.length] : null;

  return (
    <Page testId="article-page">
      <ProgressBar />

      <article className="mx-auto max-w-7xl px-6 pt-36 md:px-12 md:pt-44">
        <FadeUp y={14}>
          <Link to="/conversations" data-testid="article-back-link"
            className="group inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-ash transition-colors duration-300 hover:text-clay">
            <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
            Toutes les conversations
          </Link>
        </FadeUp>

        <div className="mt-12 max-w-4xl">
          <FadeUp delay={0.1}>
            <p className="flex flex-wrap items-center gap-x-3 text-xs font-medium uppercase tracking-[0.2em] text-clay">
              <span>{article.serie}</span>
              <span className="text-ash">·</span>
              <span className="text-ash">{article.cat}</span>
              {(article.time || article.date) && (
                <>
                  <span className="text-ash">·</span>
                  <span className="flex items-center gap-1.5 text-ash">
                    <Clock className="h-3.5 w-3.5" /> {[article.time, article.date].filter(Boolean).join(" · ")}
                  </span>
                </>
              )}
            </p>
          </FadeUp>
          <h1 data-testid="article-title" className="mt-8 font-serif text-4xl font-light leading-[1.12] tracking-tight md:text-6xl">
            <MaskedLine delay={0.25}>{article.title}</MaskedLine>
          </h1>
          {article.dek && (
            <FadeUp delay={0.5}>
              <p className="mt-8 max-w-2xl font-serif text-xl font-light italic leading-relaxed text-smoke md:text-2xl">
                {article.dek}
              </p>
            </FadeUp>
          )}
        </div>
      </article>

      {article.img && (
        <FadeUp delay={0.2} y={40} className="mx-auto mt-16 max-w-6xl px-6 md:px-12">
          <div className="relative">
            <div className="absolute -bottom-6 -right-6 hidden h-full w-full rounded-[1.75rem] bg-sand/60 md:block" aria-hidden="true" />
            <div className="relative aspect-[16/8] overflow-hidden rounded-[1.75rem]">
              <img src={article.img} alt="" className="h-full w-full object-cover" />
            </div>
          </div>
        </FadeUp>
      )}

      <div data-testid="article-body" className="mx-auto max-w-2xl px-6 py-20 md:py-28">
        {article.content.map((block, i) => {
          if (block.type === "h2") {
            return (
              <FadeUp key={i}>
                <h2 className="mt-14 font-serif text-2xl font-light tracking-tight text-charcoal md:text-3xl">
                  {block.text}
                </h2>
              </FadeUp>
            );
          }
          if (block.type === "quote") {
            return (
              <FadeUp key={i}>
                <blockquote className="my-14 border-l-2 border-clay pl-8 font-serif text-2xl font-light italic leading-relaxed text-clay md:text-3xl">
                  {block.text}
                </blockquote>
              </FadeUp>
            );
          }
          return (
            <FadeUp key={i}>
              <p className="mt-7 text-base leading-loose text-smoke md:text-lg">{block.text}</p>
            </FadeUp>
          );
        })}

        <FadeUp>
          <p className="mt-16 font-hand text-3xl text-clay md:text-4xl">Eugénie</p>
          <p className="mt-2 text-xs uppercase tracking-[0.2em] text-ash">
            Sexologue psychothérapeute · Entrepreneure sociale · Vulgarisatrice
          </p>
        </FadeUp>

        <FadeUp delay={0.05}>
          <ShareButtons title={article.title} />
        </FadeUp>

        <FadeUp delay={0.1}>
          <div className="mt-14 rounded-[1.75rem] bg-sage/40 p-10">
            <h2 className="font-serif text-2xl font-light tracking-tight md:text-3xl">
              Ce sujet vous <em className="text-clay">questionne ?</em>
            </h2>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-smoke md:text-base">
              Chaque conversation commence quelque part. La vôtre peut commencer ici.
            </p>
            <Link to="/contact" data-testid="article-cta-contact"
              className="group mt-8 inline-flex items-center gap-2 rounded-full bg-clay px-7 py-3.5 text-xs font-medium uppercase tracking-[0.15em] text-cream transition-colors duration-300 hover:bg-charcoal">
              On peut en parler
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </FadeUp>
      </div>

      {next && (
        <section data-testid="article-next-section" className="border-t border-linen py-20">
          <div className="mx-auto max-w-7xl px-6 md:px-12">
            <FadeUp><Meta>La conversation continue</Meta></FadeUp>
            <Link to={`/conversations/${next.slug}`} data-testid="article-next-link" className="group mt-8 block">
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-clay">{next.serie}</p>
              <h2 className="mt-4 max-w-3xl font-serif text-3xl font-light leading-snug tracking-tight transition-colors duration-300 group-hover:text-clay md:text-5xl">
                {next.title}
              </h2>
              <span className="mt-6 inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.15em] text-smoke transition-colors duration-300 group-hover:text-clay">
                Lire la suite
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </span>
            </Link>
          </div>
        </section>
      )}
    </Page>
  );
}
